import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import api from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const tokenExpiryTimer = useRef(null);

  // JWT token expiry check - 8 hours = 28800000 ms
  const JWT_EXPIRY_TIME = 8 * 60 * 60 * 1000;

  const scheduleTokenExpiry = () => {
    // Clear existing timer
    if (tokenExpiryTimer.current) {
      clearTimeout(tokenExpiryTimer.current);
    }

    // Set new timer for 8 hours
    tokenExpiryTimer.current = setTimeout(() => {
      handleTokenExpired();
    }, JWT_EXPIRY_TIME);
  };

  const handleTokenExpired = async () => {
    toast.warning('Your session has expired. Please login again.');
    
    try {
      await api.post('/auth/logout', { reason: 'jwt_expired' });
    } catch (error) {
      console.error('Logout on token expiry failed:', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenTimestamp');
    setUser(null);
    setIsAuthenticated(false);
    
    // Force redirect to login
    window.location.href = '/login';
  };

  // Check for token expiry on mount and set up timer
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('token');
      const tokenTimestamp = localStorage.getItem('tokenTimestamp');

      if (token && tokenTimestamp) {
        const elapsed = Date.now() - parseInt(tokenTimestamp, 10);
        
        if (elapsed >= JWT_EXPIRY_TIME) {
          // Token has expired
          handleTokenExpired();
        } else {
          // Schedule expiry for remaining time
          const remaining = JWT_EXPIRY_TIME - elapsed;
          tokenExpiryTimer.current = setTimeout(() => {
            handleTokenExpired();
          }, remaining);
        }
      }
    };

    checkTokenExpiry();

    // Cleanup timer on unmount
    return () => {
      if (tokenExpiryTimer.current) {
        clearTimeout(tokenExpiryTimer.current);
      }
    };
  }, []);

  // Setup axios interceptor to handle 403 errors (token expired)
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 403 && error.response?.data?.expired) {
          handleTokenExpired();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Failed to parse user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tokenTimestamp', Date.now().toString());
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Schedule token expiry
      scheduleTokenExpiry();
      
      toast.success(`Welcome back, ${userData.username}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token, user: newUser } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      setUser(newUser);
      setIsAuthenticated(true);
      
      toast.success(`Welcome, ${newUser.username}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', { reason: 'manual' });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    // Clear timer
    if (tokenExpiryTimer.current) {
      clearTimeout(tokenExpiryTimer.current);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenTimestamp');
    setUser(null);
    setIsAuthenticated(false);
    toast.info('Logged out successfully');
  };

  const updateUserStatus = (status) => {
    if (user) {
      const updatedUser = { ...user, status };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUserStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
